import { useState } from 'react';

import ProgressBar from '@/components/ProgressBar';
import { AnswerType, Industry, QuestionModule } from '@/enums';
import { useBreakpoint } from '@/hooks/useMediaQuery';
import { QuestionWithAnswer } from '@/types';
import { formatQuestionNumber } from '@/utils/common';
import { getIndustryHamburgerColor } from '@/utils/industryColors';

import * as Style from './HamburgerMenu.style';
import Marquee from './Marquee';

interface Props {
  questions: QuestionWithAnswer[];
  answersMap: Map<string, string>;
  module: QuestionModule;
  industry?: Industry;
  onNavigate: (index: number, questionId: string) => void;
  currentStep?: number;
  totalSteps?: number;
}

const HamburgerMenu = ({
  questions,
  answersMap,
  module,
  industry,
  onNavigate,
  currentStep,
  totalSteps,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const { isMobileOrTablet } = useBreakpoint();

  const getDisplayQuestionNumber = (question: QuestionWithAnswer): string => {
    if (question.level === 1) {
      return formatQuestionNumber(question.order);
    }

    const parentQuestion = questions.find(
      (q) => q.id === question.parentQuestionId,
    );

    return formatQuestionNumber(
      parentQuestion ? parentQuestion.order : question.order,
    );
  };

  const shouldShowChildQuestion = (
    question: QuestionWithAnswer,
    allQuestions: QuestionWithAnswer[],
    answersMap: Map<string, string>,
  ): boolean => {
    if (question.level <= 1) return true;

    const parent = question.parentQuestionId
      ? allQuestions.find((q) => q.id === question.parentQuestionId)
      : allQuestions.find((q) => q.level === question.level - 1);

    if (!parent) return true;
    return ['3', '4', '5'].includes(answersMap.get(parent.id) || '');
  };

  const moduleMapping = {
    [QuestionModule.Common]: '基本資料',
    [QuestionModule.Business]: '業務範疇',
    [QuestionModule.SDGs]: 'SDGs',
  };

  const filteredQuestions = questions.filter(
    (q) => q.answerType !== AnswerType.Info,
  );

  // Define item type
  interface ChildItem {
    key: string;
    label: string;
    description: string;
    disabled: boolean;
    onClick: () => void;
  }

  interface MenuItem {
    key: string;
    label: string | number;
    level: number;
    module: QuestionModule;
    description: string;
    disabled: boolean;
    children?: ChildItem[]; // Child questions with full information
    onClick: () => void;
  }

  // Organize questions with parent-child relationship
  const organizeQuestions = (): MenuItem[] => {
    const organized: MenuItem[] = [];
    const parentQuestions = filteredQuestions.filter((q) => q.level === 1);

    parentQuestions.forEach((parent) => {
      // Get child questions for this parent
      const childQuestions = filteredQuestions.filter(
        (q) => q.parentQuestionId === parent.id && q.level > 1,
      );

      const children: ChildItem[] = [];

      childQuestions.forEach((child, index) => {
        const shouldShow = shouldShowChildQuestion(
          child,
          questions,
          answersMap,
        );

        if (shouldShow) {
          children.push({
            key: child.id,
            label: `${formatQuestionNumber(parent.order)}-${index + 1}`,
            description: child.question,
            disabled: !answersMap.has(child.id),
            onClick: () => {
              if (answersMap.has(child.id)) {
                const questionIndex = questions.findIndex(
                  (q) => q.id === child.id,
                );
                onNavigate(questionIndex, child.id);
                setOpen(false);
              }
            },
          });
        }
      });

      const parentItem: MenuItem = {
        key: parent.id,
        label: getDisplayQuestionNumber(parent),
        level: parent.level,
        module,
        description: parent.question,
        disabled: !answersMap.has(parent.id),
        children: children.length > 0 ? children : undefined,
        onClick: () => {
          if (answersMap.has(parent.id)) {
            const questionIndex = questions.findIndex(
              (q) => q.id === parent.id,
            );
            onNavigate(questionIndex, parent.id);
            setOpen(false);
          }
        },
      };
      organized.push(parentItem);
    });

    return organized;
  };

  const items = organizeQuestions();

  const showProgressBar =
    isMobileOrTablet &&
    currentStep !== undefined &&
    totalSteps !== undefined &&
    totalSteps > 0;

  return (
    <Style.HamburgerMenuContainer>
      <Style.MenuToggle
        type="text"
        isOpen={open}
        onClick={() => setOpen((prev) => !prev)}
        aria-label="Menu"
      >
        <Style.Hamburger isOpen={open}>
          <span />
          <span />
          <span />
        </Style.Hamburger>
      </Style.MenuToggle>

      <Style.Overlay isOpen={open} onClick={() => setOpen(false)} />

      {showProgressBar && open && (
        <ProgressBar
          currentStep={currentStep}
          totalSteps={totalSteps}
          industry={industry}
          style={{ left: '12px' }}
        />
      )}

      <Style.MenuItems isOpen={open}>
        {items.map((item) => (
          <Style.MenuItemGroup
            key={item.key}
            onMouseEnter={() => setHoveredItem(item.key)}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <Style.StyledMenuItem
              type="text"
              shape="round"
              disabled={item.disabled}
              $color={getIndustryHamburgerColor(industry).color}
              $colorHover={getIndustryHamburgerColor(industry).colorHover}
              $bgHover={getIndustryHamburgerColor(industry).bgHover}
              onClick={item.onClick}
            >
              <Style.MenuItemContent>
                <Style.MenuItemTitle>
                  {item.label}
                  {module && (
                    <Style.MenuItemModule>
                      {moduleMapping[module]}
                    </Style.MenuItemModule>
                  )}
                </Style.MenuItemTitle>
                <Style.MenuItemDescription>
                  <Marquee text={item.description} speed={30} />
                </Style.MenuItemDescription>
              </Style.MenuItemContent>
            </Style.StyledMenuItem>
            {item.children && item.children.length > 0 && (
              <Style.ChildrenContainer $isVisible={hoveredItem === item.key}>
                {item.children.map((child) => (
                  <Style.StyledMenuItem
                    key={child.key}
                    type="text"
                    shape="round"
                    disabled={child.disabled}
                    $color={getIndustryHamburgerColor(industry).color}
                    $colorHover={getIndustryHamburgerColor(industry).colorHover}
                    $bgHover={getIndustryHamburgerColor(industry).bgHover}
                    onClick={child.onClick}
                  >
                    <Style.MenuItemContent>
                      <Style.MenuItemTitle>{child.label}</Style.MenuItemTitle>
                      <Style.MenuItemDescription>
                        <Marquee text={child.description} speed={30} />
                      </Style.MenuItemDescription>
                    </Style.MenuItemContent>
                  </Style.StyledMenuItem>
                ))}
              </Style.ChildrenContainer>
            )}
          </Style.MenuItemGroup>
        ))}
      </Style.MenuItems>
    </Style.HamburgerMenuContainer>
  );
};

export default HamburgerMenu;
